const TEMPLATE = document.createElement("template");

TEMPLATE.innerHTML = `
  <style>
    :host {
      display: block;
      /* 高度跟随 iframe 自适应；仅当显式指定时才应用固定高度 /
         Height follows iframe intrinsic content; honor an explicit var only when set. */
      height: var(--md2pdf-viewer-height, auto);
      border: var(--md2pdf-viewer-border, 1px solid #d4d4d4);
      color: var(--md2pdf-viewer-fg, #171717);
      background: var(--md2pdf-viewer-bg, #ffffff);
      font-family: Inter, ui-sans-serif, system-ui, sans-serif;
    }

    .root {
      display: grid;
      grid-template-rows: auto minmax(0, auto);
      min-height: 0;
    }

    .toolbar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px;
      border-bottom: 1px solid #d4d4d4;
      background: #fafafa;
    }

    .toolbar button {
      border: 1px solid #c8c8c8;
      background: #ffffff;
      padding: 5px 10px;
      font: inherit;
      cursor: pointer;
    }

    .toolbar button[aria-pressed="true"] {
      color: #ffffff;
      border-color: var(--md2pdf-viewer-accent, #0a0a0b);
      background: var(--md2pdf-viewer-accent, #0a0a0b);
    }

    .panels {
      min-height: 0;
      display: grid;
    }

    :host([view="split"]) .panels {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }

    textarea.source {
      width: 100%;
      height: var(--md2pdf-source-height, 480px);
      min-height: 0;
      resize: none;
      border: 0;
      padding: 16px;
      font: 13px/1.55 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      box-sizing: border-box;
    }

    iframe.preview {
      display: block;
      width: 100%;
      /* 初始兜底高度，JS 在内容就绪后会根据 iframe 内部实际高度覆盖 /
         Placeholder height; JS overrides it after content-size postMessage. */
      height: var(--md2pdf-viewer-initial-height, 480px);
      border: 0;
      background: #ffffff;
    }

    .hidden {
      display: none !important;
    }

    .diagnostics {
      padding: 8px 12px;
      border-top: 1px solid #d4d4d4;
      font-size: 13px;
      line-height: 1.4;
    }

    .diagnostics:empty {
      display: none;
    }

    .diagnostic-error {
      color: #991b1b;
    }
  </style>
  <div class="root">
    <div class="toolbar">
      <button type="button" data-view="source">Source</button>
      <button type="button" data-view="preview">Preview</button>
      <button type="button" data-view="split">Split</button>
      <slot name="toolbar-extra"></slot>
    </div>
    <div class="panels">
      <textarea class="source"></textarea>
      <iframe class="preview" sandbox="allow-scripts"></iframe>
    </div>
    <div class="diagnostics" aria-live="polite"></div>
  </div>
`;

const DEFAULTS = {
  markdown: "",
  renderer: null,
  view: "preview",
  theme: "monocle",
  reportMode: "auto",
  debounceMs: 400,
};

export class Md2PdfReportViewer extends HTMLElement {
  static get observedAttributes() {
    return ["view", "editable", "theme", "report-mode", "debounce-ms", "controls"];
  }

  constructor() {
    super();
    this._markdown = DEFAULTS.markdown;
    this._renderer = DEFAULTS.renderer;
    this._view = DEFAULTS.view;
    this._theme = DEFAULTS.theme;
    this._reportMode = DEFAULTS.reportMode;
    this._debounceMs = DEFAULTS.debounceMs;
    this._onViewButtonClick = this._onViewButtonClick.bind(this);
    this._onSourceInput = this._onSourceInput.bind(this);
    this._onPreviewMessage = this._onPreviewMessage.bind(this);
    this._viewButtonsWired = false;
    this._sourceInputWired = false;
    this._messageWired = false;
    this._renderTimer = null;
    this._renderId = 0;
    this._lastResult = null;
    this._outlineHeadings = [];
    this.attachShadow({ mode: "open" });
    this.shadowRoot.append(TEMPLATE.content.cloneNode(true));
  }

  connectedCallback() {
    this._upgradeFromAttributes();
    this._source = this.shadowRoot.querySelector(".source");
    if (!this._viewButtonsWired) {
      this.shadowRoot.querySelectorAll("[data-view]").forEach((button) => {
        button.addEventListener("click", this._onViewButtonClick);
      });
      this._viewButtonsWired = true;
    }
    if (!this._sourceInputWired) {
      this._source.addEventListener("input", this._onSourceInput);
      this._sourceInputWired = true;
    }
    if (!this._messageWired) {
      window.addEventListener("message", this._onPreviewMessage);
      this._messageWired = true;
    }
    this._renderChrome();
  }

  disconnectedCallback() {
    this._renderId += 1;
    window.clearTimeout(this._renderTimer);
    this._renderTimer = null;
    if (this._messageWired) {
      window.removeEventListener("message", this._onPreviewMessage);
      this._messageWired = false;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }

    if (name === "view") {
      this._view = this._normalizeView(newValue);
    } else if (name === "theme") {
      this._theme = newValue || DEFAULTS.theme;
    } else if (name === "report-mode") {
      this._reportMode = newValue || DEFAULTS.reportMode;
    } else if (name === "debounce-ms") {
      const parsed = Number.parseInt(newValue, 10);
      this._debounceMs = Number.isFinite(parsed) ? parsed : DEFAULTS.debounceMs;
    } else if (name === "editable" || name === "controls") {
      // Chrome state is derived directly from the current attributes.
    }

    this._renderChrome();
  }

  get markdown() {
    return this._markdown;
  }

  set markdown(value) {
    this.setMarkdown(value);
  }

  get outlineHeadings() {
    return this._outlineHeadings.slice();
  }

  set outlineHeadings(value) {
    this.setOutlineHeadings(value);
  }

  get renderer() {
    return this._renderer;
  }

  set renderer(value) {
    this.setRenderer(value);
  }

  get view() {
    return this._view;
  }

  set view(value) {
    const nextView = this._normalizeView(value);
    if (this._view === nextView) {
      return;
    }

    this._view = nextView;
    this.setAttribute("view", nextView);
    this._dispatch("viewchange", { view: nextView });
    this._renderChrome();
  }

  setMarkdown(markdown, options = {}) {
    this._markdown = String(markdown ?? "");
    const source = this.shadowRoot.querySelector(".source");
    if (source && source.value !== this._markdown) {
      source.value = this._markdown;
    }
    if (options.render) {
      this._scheduleRender();
    }
  }

  getMarkdown() {
    return this._markdown;
  }

  // 设置外层报告目录使用的标题映射 /
  // Set heading metadata used by the host report outline.
  setOutlineHeadings(headings) {
    this._outlineHeadings = Array.isArray(headings)
      ? headings
          .map((heading) => ({
            id: String(heading?.id ?? ""),
            level: Number.parseInt(String(heading?.level ?? ""), 10),
            text: String(heading?.text ?? ""),
          }))
          .filter((heading) => heading.id && Number.isFinite(heading.level) && heading.text)
      : [];
  }

  setRenderer(renderer) {
    this._renderer = typeof renderer === "function" ? renderer : null;
  }

  async renderNow() {
    if (!this._renderer) {
      return;
    }

    const renderId = ++this._renderId;
    const input = {
      markdown: this._markdown,
      theme: this._theme,
      reportMode: this._reportMode,
    };
    this._dispatch("md2pdf-render-start", { markdown: this._markdown, renderId });

    try {
      const result = (await this._renderer(input)) || { html: "" };
      if (renderId !== this._renderId) {
        return;
      }

      await this._writePreview(result);
      if (renderId !== this._renderId) {
        return;
      }
      this._lastResult = result;
      this._renderDiagnostics(result.diagnostics || []);
      this._dispatch("md2pdf-render-complete", { result, renderId });
    } catch (error) {
      if (renderId !== this._renderId) {
        return;
      }

      const message = error?.message || String(error);
      this._renderDiagnostics([{ level: "error", message }]);
      await this._writePreview({
        html: `<pre class="md2pdf-render-error">${this._escapeHtml(message)}</pre>`,
        css: ".md2pdf-render-error { color: #991b1b; white-space: pre-wrap; }",
      });
      if (renderId !== this._renderId) {
        return;
      }
      this._dispatch("md2pdf-render-error", { error, renderId });
    }
  }

  _scheduleRender() {
    window.clearTimeout(this._renderTimer);
    this._renderTimer = window.setTimeout(() => {
      this.renderNow();
    }, this._debounceMs);
  }

  _upgradeFromAttributes() {
    this._view = this._normalizeView(this.getAttribute("view") || this._view);
    this._theme = this.getAttribute("theme") || this._theme;
    this._reportMode = this.getAttribute("report-mode") || this._reportMode;

    const debounceMs = this.getAttribute("debounce-ms");
    if (debounceMs !== null) {
      const parsed = Number.parseInt(debounceMs, 10);
      this._debounceMs = Number.isFinite(parsed) ? parsed : this._debounceMs;
    }

    if (!this.hasAttribute("view")) {
      this.setAttribute("view", this._view);
    }
  }

  _dispatch(type, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(type, {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }

  async _writePreview(result = {}) {
    const preview = this.shadowRoot.querySelector(".preview");
    if (!preview) {
      return;
    }

    const head = result.head || "";
    const html = result.html || "";
    const css = result.css ? `<style>${result.css}</style>` : "";
    const scripts = Array.isArray(result.scripts)
      ? result.scripts
          .map((script) => `<script>${this._escapeScriptEndTags(script)}<\/script>`)
          .join("")
      : "";
    // 让 iframe 内部文档高度跟随内容，并通过 postMessage 汇报给外层组件 /
    // Make iframe document flow natively and report size up via postMessage.
    const resizeCss =
      "<style>html,body{margin:0;}body{overflow:hidden;}</style>";
    const resizeScript = this._buildResizeScript();
    // 挂上 ripple-pmf-report class，让 overlay.css 中基于该 class 的选择器在 preview
    // 路径（非完整 render_html 路径）下也能命中，保证表头 nowrap / echarts 容器等样式生效 /
    // Apply the ripple-pmf-report class on the preview body so overlay.css rules
    // scoped to that class also match here (render_html path already does this).
    const outlineScript = this._buildOutlineScript();
    const srcdoc = `<!doctype html><html><head><meta charset="utf-8">${head}${css}${resizeCss}</head><body class="ripple-pmf-report">${html}${scripts}${outlineScript}${resizeScript}</body></html>`;

    await new Promise((resolve) => {
      const timeoutId = window.setTimeout(done, 1000);
      function done() {
        window.clearTimeout(timeoutId);
        preview.removeEventListener("load", done);
        resolve();
      }
      preview.addEventListener("load", done, { once: true });
      preview.srcdoc = srcdoc;
    });
  }

  // 注入目录定位脚本：标记标题并响应外层滚动请求 /
  // Inject outline script: tag headings and answer host scroll-offset requests.
  _buildOutlineScript() {
    const headings = this._outlineHeadings.map((heading) => ({
      id: heading.id,
      level: heading.level,
      text: heading.text,
    }));
    if (!headings.length) {
      return "";
    }
    const headingsJson = this._escapeScriptEndTags(JSON.stringify(headings));
    const body = [
      "(function(){",
      "  try {",
      `    var outlineHeadings = ${headingsJson};`,
      "    function tagHeadings(){",
      "      var levelMap = {};",
      "      outlineHeadings.forEach(function(item){ levelMap[item.level] = true; });",
      "      var selector = Object.keys(levelMap).map(function(level){ return 'h' + level; }).join(',');",
      "      if (!selector) return;",
      "      var nodes = Array.prototype.slice.call(document.querySelectorAll(selector));",
      "      function normalize(value){",
      "        return String(value || '')",
      "          .replace(/\\s+/g, ' ')",
      "          .trim();",
      "      }",
      "      function findNode(item, index){",
      "        var text = normalize(item.text);",
      "        for (var i = 0; i < nodes.length; i += 1) {",
      "          if (nodes[i].getAttribute('data-report-outline-id')) continue;",
      "          if (normalize(nodes[i].textContent) === text) return nodes[i];",
      "        }",
      "        for (var j = 0; j < nodes.length; j += 1) {",
      "          if (!nodes[j].getAttribute('data-report-outline-id')) return nodes[j];",
      "        }",
      "        return nodes[index] || null;",
      "      }",
      "      outlineHeadings.forEach(function(item, index){",
      "        var node = findNode(item, index);",
      "        if (!node) return;",
      "        node.id = item.id;",
      "        node.setAttribute('data-report-outline-id', item.id);",
      "      });",
      "    }",
      "    function findTarget(id){",
      "      var nodes = document.querySelectorAll('[data-report-outline-id]');",
      "      for (var i = 0; i < nodes.length; i += 1) {",
      "        if (nodes[i].getAttribute('data-report-outline-id') === id) return nodes[i];",
      "      }",
      "      return null;",
      "    }",
      "    tagHeadings();",
      "    window.addEventListener('message', function(event){",
      "      var data = event.data;",
      "      if (!data || data.type !== 'md2pdf:outline-offset-request') return;",
      "      var target = findTarget(String(data.targetId || ''));",
      "      if (!target) return;",
      "      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;",
      "      var offsetTop = target.getBoundingClientRect().top + scrollTop;",
      "      try {",
      "        parent.postMessage({",
      "          type: 'md2pdf:outline-offset',",
      "          targetId: data.targetId,",
      "          requestId: data.requestId,",
      "          offsetTop: offsetTop",
      "        }, '*');",
      "      } catch (e) {}",
      "    });",
      "  } catch (err) { /* noop */ }",
      "})();",
    ].join("\n");
    return `<script>${this._escapeScriptEndTags(body)}<\/script>`;
  }

  // 注入到 iframe 的自适应高度脚本 /
  // Auto-size script injected into the iframe srcdoc.
  _buildResizeScript() {
    const body = [
      "(function(){",
      "  try {",
      "    var last = 0;",
      "    function measure(){",
      "      var doc = document.documentElement;",
      "      var body = document.body;",
      "      var h = Math.max(",
      "        doc ? doc.scrollHeight : 0,",
      "        body ? body.scrollHeight : 0,",
      "        doc ? doc.offsetHeight : 0,",
      "        body ? body.offsetHeight : 0",
      "      );",
      "      if (Math.abs(h - last) >= 1) {",
      "        last = h;",
      "        try { parent.postMessage({ type: 'md2pdf:size', height: h }, '*'); } catch (e) {}",
      "      }",
      "    }",
      "    function ping(){ measure(); }",
      "    window.addEventListener('load', ping);",
      "    if (document.readyState === 'complete' || document.readyState === 'interactive') { ping(); }",
      "    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {",
      "      document.fonts.ready.then(ping).catch(function(){});",
      "    }",
      "    if (typeof ResizeObserver === 'function') {",
      "      var ro = new ResizeObserver(ping);",
      "      if (document.documentElement) ro.observe(document.documentElement);",
      "      if (document.body) ro.observe(document.body);",
      "    }",
      "    var mo = new MutationObserver(ping);",
      "    if (document.body) mo.observe(document.body, { subtree: true, childList: true, attributes: true });",
      "    window.addEventListener('resize', ping);",
      "    var retry = 0;",
      "    var timer = setInterval(function(){ retry += 1; ping(); if (retry > 20) clearInterval(timer); }, 250);",
      "  } catch (err) { /* noop */ }",
      "})();",
    ].join("\n");
    return `<script>${this._escapeScriptEndTags(body)}<\/script>`;
  }

  // 接收 iframe 的高度汇报并写回宿主 /
  // Receive size messages from the iframe and apply to the preview element.
  _onPreviewMessage(event) {
    const preview = this.shadowRoot && this.shadowRoot.querySelector(".preview");
    if (!preview) return;
    if (event.source !== preview.contentWindow) return;
    const data = event.data;
    if (!data || data.type !== "md2pdf:size") return;
    const height = Number(data.height);
    if (!Number.isFinite(height) || height <= 0) return;
    const next = Math.ceil(height);
    // 避免 1px 抖动 / Ignore sub-pixel oscillation.
    if (this._lastPreviewHeight && Math.abs(next - this._lastPreviewHeight) < 1) return;
    this._lastPreviewHeight = next;
    preview.style.height = `${next}px`;
  }

  _escapeScriptEndTags(value) {
    return String(value).replace(/<\/script/gi, (tag) => `<\\/${tag.slice(2)}`);
  }

  _escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  _renderDiagnostics(items) {
    const diagnostics = this.shadowRoot.querySelector(".diagnostics");
    if (!diagnostics) {
      return;
    }

    diagnostics.replaceChildren(
      ...items.map((item) => {
        const row = document.createElement("div");
        row.className = `diagnostic diagnostic-${item.level || "info"}`;
        row.textContent = item.message || "";
        return row;
      }),
    );
  }

  _renderChrome() {
    if (!this.shadowRoot) {
      return;
    }

    const toolbar = this.shadowRoot.querySelector(".toolbar");
    const source = this.shadowRoot.querySelector(".source");
    const preview = this.shadowRoot.querySelector(".preview");
    this.shadowRoot.querySelectorAll("[data-view]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.view === this._view));
    });

    toolbar?.classList.toggle("hidden", this.getAttribute("controls") === "false");
    source?.classList.toggle("hidden", this._view === "preview");
    if (source) {
      source.readOnly = !this.hasAttribute("editable");
    }
    preview?.classList.toggle("hidden", this._view === "source");
  }

  _normalizeView(value) {
    return ["source", "preview", "split"].includes(value) ? value : DEFAULTS.view;
  }

  _onViewButtonClick(event) {
    this.view = event.currentTarget.dataset.view;
  }

  _onSourceInput() {
    this._markdown = this._source.value;
    this._dispatch("md2pdf-change", { markdown: this._markdown });
    this._scheduleRender();
  }
}

if (!customElements.get("md2pdf-report-viewer")) {
  customElements.define("md2pdf-report-viewer", Md2PdfReportViewer);
}
